import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, PhoneIcon, MessageSquareIcon, ClipboardCheckIcon, UserCheckIcon, PhoneCallIcon } from 'lucide-react';
export function ThankYouPage() {
  useEffect(() => {
    document.title = 'Thank You - School Launch';
    window.scrollTo(0, 0);
  }, []);
  return <div className="min-h-screen bg-black text-white font-sans selection:bg-amber-500 selection:text-black" data-id="element-387">
      {/* Unified Hero Section (Above the Fold) */}
      <section className="min-h-[90vh] flex flex-col relative overflow-hidden" data-id="element-388">
        {/* Dot Grid Texture */}
        <div className="absolute inset-0 opacity-[0.10] bg-[radial-gradient(circle_at_center,rgb(245,158,11)_1px,transparent_1px)] bg-[size:20px_20px]" aria-hidden="true" data-id="element-389" />

        {/* Logo */}
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4 relative z-10" data-id="element-390">
          <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-8 w-auto" data-id="element-391" />
        </div>

        {/* Hero Content */}
        <div className="flex-1 flex flex-col items-center justify-start px-4 sm:px-6 lg:px-8 pt-8 pb-1 relative z-10" data-id="element-392">
          <div className="max-w-3xl mx-auto text-center w-full" data-id="element-393">
            {/* Animated Check Icon with Sonar Rings */}
            <div className="w-24 h-24 mx-auto mb-4 relative flex items-center justify-center" data-id="element-394">
              {/* Sonar Ring 1 */}
              <motion.div className="absolute inset-0 rounded-full border border-amber-500/60" initial={{
              scale: 1,
              opacity: 0.2
            }} animate={{
              scale: 1.8,
              opacity: 0
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeOut',
              delay: 0
            }} data-id="element-395" />
              {/* Sonar Ring 2 */}
              <motion.div className="absolute inset-0 rounded-full border border-amber-500/60" initial={{
              scale: 1,
              opacity: 0.2
            }} animate={{
              scale: 1.8,
              opacity: 0
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeOut',
              delay: 0.5
            }} data-id="element-396" />
              {/* Sonar Ring 3 */}
              <motion.div className="absolute inset-0 rounded-full border border-amber-500/60" initial={{
              scale: 1,
              opacity: 0.2
            }} animate={{
              scale: 1.8,
              opacity: 0
            }} transition={{
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3,
              ease: 'easeOut',
              delay: 1
            }} data-id="element-397" />

              {/* Center Check */}
              <motion.div initial={{
              scale: 0,
              rotate: -90
            }} animate={{
              scale: 1,
              rotate: 0
            }} transition={{
              type: 'spring',
              stiffness: 150,
              damping: 18,
              duration: 0.8
            }} className="relative z-10 w-20 h-20 rounded-full bg-amber-500/10 border border-amber-500/40 flex items-center justify-center" data-id="element-398">
                <CheckCircle2Icon className="h-10 w-10 text-amber-500" strokeWidth={2} data-id="element-399" />
              </motion.div>
            </div>

            {/* Headline */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2,
            duration: 0.5
          }} className="mb-3 leading-tight" data-id="element-400">
              <span className="block text-4xl md:text-6xl font-extrabold text-white mb-1" data-id="element-401">
                You're Booked.
              </span>
              <span className="block text-3xl md:text-5xl font-light text-zinc-300" data-id="element-402">
                Your consultation is scheduled.
              </span>
            </motion.h1>

            {/* Subtitle / Warning */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="text-lg text-zinc-400 mb-4 max-w-2xl mx-auto leading-relaxed" data-id="element-403">
              We'll be reaching out to you shortly. Please check your email for
              confirmation details and stay near your phone —{' '}
              <span className="text-amber-500 font-medium" data-id="element-404">
                you may receive a call from an unknown number.
              </span>
            </motion.p>

            {/* Confirmation Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.5
          }} className="w-full" data-id="element-405">
              <p className="text-sm text-zinc-500 mb-3 font-medium uppercase tracking-wider" data-id="element-406">
                Confirm you'll be available:
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-407">
                <button onClick={() => {
                alert('SMS confirmation will be enabled once you provide the phone number');
              }} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-amber-500/20" data-id="element-408">
                  <MessageSquareIcon className="h-5 w-5" data-id="element-409" />
                  Confirm via Text
                </button>

                <button onClick={() => {
                alert('WhatsApp confirmation will be enabled once you provide the WhatsApp number');
              }} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-all duration-200 hover:scale-105 shadow-lg shadow-green-600/20" data-id="element-410">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-411">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-412" />
                  </svg>
                  Confirm via WhatsApp
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What Happens Next Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-zinc-950 border-y border-zinc-900" data-id="element-413">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.6,
        duration: 0.5
      }} className="max-w-4xl mx-auto" data-id="element-414">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12" data-id="element-415">
            What Happens Next
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-416">
            {[{
            icon: ClipboardCheckIcon,
            step: '1',
            title: 'We Review Your Info',
            description: "Our team carefully reviews your application to understand your center's needs and goals."
          }, {
            icon: PhoneCallIcon,
            step: '2',
            title: 'We Reach Out',
            description: 'An enrollment specialist will call you within the next few hours to discuss your growth plan.'
          }, {
            icon: UserCheckIcon,
            step: '3',
            title: 'Strategy Discussion',
            description: "We'll walk you through our proven system and how we can help fill your empty seats."
          }].map((item, i) => <motion.div key={item.step} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.7 + i * 0.1,
            duration: 0.5
          }} className="text-center" data-id="element-417">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-500/10 border-2 border-amber-500 flex items-center justify-center" data-id="element-418">
                  <item.icon className="h-8 w-8 text-amber-500" data-id="element-419" />
                </div>
                <div className="text-sm font-bold text-amber-500 mb-2" data-id="element-420">
                  Step {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2" data-id="element-421">{item.title}</h3>
                <p className="text-zinc-400 leading-relaxed" data-id="element-422">
                  {item.description}
                </p>
              </motion.div>)}
          </div>
        </motion.div>
      </section>

      {/* Social Proof Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8" data-id="element-423">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.9,
        duration: 0.5
      }} className="max-w-2xl mx-auto text-center" data-id="element-424">
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-6 md:p-8" data-id="element-425">
            <p className="text-lg text-zinc-300 italic leading-relaxed mb-4" data-id="element-426">
              "Within 60 days of working with School Launch, we had to start a
              waitlist. The system works — you just have to show up for the
              call."
            </p>
            <div className="text-amber-500 font-bold" data-id="element-427">
              — Maria Lopez, Director at Bright Futures Preschool
            </div>
          </div>
        </motion.div>
      </section>

      {/* Final Reminder Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-amber-400 via-amber-500 to-amber-600 text-center relative overflow-hidden" data-id="element-428">
        <div className="absolute inset-0 opacity-[0.04] bg-[radial-gradient(circle_at_center,black_1.5px,transparent_1.5px)] bg-[size:16px_16px]" aria-hidden="true" data-id="element-429" />
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 1,
        duration: 0.5
      }} className="max-w-2xl mx-auto text-center relative z-10" data-id="element-430">
          <PhoneIcon className="h-12 w-12 text-black mx-auto mb-4" data-id="element-431" />
          <h2 className="text-2xl md:text-3xl font-bold mb-4 text-black" data-id="element-432">
            One Last Reminder
          </h2>
          <p className="text-amber-950 mb-8 leading-relaxed" data-id="element-433">
            Please keep your phone nearby. Our team is excited to speak with you
            and show you exactly how we can help fill your center to capacity.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-434">
            <button onClick={() => {
            alert('SMS confirmation will be enabled once you provide the phone number');
          }} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-black hover:bg-zinc-900 text-white font-bold rounded-lg transition-all duration-200 hover:scale-105 shadow-2xl shadow-black/25" data-id="element-435">
              <MessageSquareIcon className="h-5 w-5" data-id="element-436" />
              Confirm via Text
            </button>

            <button onClick={() => {
            alert('WhatsApp confirmation will be enabled once you provide the WhatsApp number');
          }} className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-black/80 hover:bg-black text-white font-bold rounded-lg transition-all duration-200 hover:scale-105 shadow-2xl shadow-black/25" data-id="element-437">
              <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" data-id="element-438">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" data-id="element-439" />
              </svg>
              Confirm via WhatsApp
            </button>
          </div>
        </motion.div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-12 border-t border-zinc-900" data-id="element-440">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-441">
          <img src="https://cdn.magicpatterns.com/uploads/3aM3DA6U6f25P9EHkUZYxu/SCHOOL_LAUNCH_LOGO_black_2.png" alt="SchoolLaunch" className="h-8 w-auto mx-auto mb-4" data-id="element-442" />
          <div className="text-zinc-500 text-sm" data-id="element-443">
            &copy; {new Date().getFullYear()} School Launch Agency. All rights
            reserved.
          </div>
        </div>
      </footer>
    </div>;
}